import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { FileText, Upload, Trash2, Eye, EyeOff, Pencil, X, Download, FolderOpen } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { catalogsAPI } from '../lib/api';
import { toast } from 'sonner';

const CATALOG_TYPES = {
    catalog: { label: 'Catalogue', color: 'bg-blue-100 text-blue-800' },
    flyer: { label: 'Flyer', color: 'bg-green-100 text-green-800' },
    poster: { label: 'Affiche', color: 'bg-purple-100 text-purple-800' },
};

const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
};

const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
    });
};

export const AdminCatalogs = () => {
    const [catalogs, setCatalogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isUploadDialogOpen, setIsUploadDialogOpen] = useState(false);
    const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const [selectedCatalog, setSelectedCatalog] = useState(null);
    const [uploading, setUploading] = useState(false);
    const fileInputRef = useRef(null);
    
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        catalog_type: 'catalog',
        file: null
    });

    const fetchCatalogs = async () => {
        try {
            const response = await catalogsAPI.list();
            setCatalogs(response.data.catalogs || []);
        } catch (error) {
            console.error('Error fetching catalogs:', error);
            toast.error('Erreur lors du chargement des catalogues');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchCatalogs();
    }, []);

    const handleUpload = async (e) => {
        e.preventDefault();
        if (!formData.file) {
            toast.error('Veuillez sélectionner un fichier PDF');
            return;
        }
        
        // Validate file type
        if (!formData.file.name.toLowerCase().endsWith('.pdf')) {
            toast.error('Seuls les fichiers PDF sont acceptés');
            return;
        }
        
        // Validate file size (max 50MB)
        const maxSize = 50 * 1024 * 1024;
        if (formData.file.size > maxSize) {
            toast.error('Le fichier est trop volumineux (max 50 Mo)');
            return;
        }
        
        setUploading(true);
        toast.info('Upload en cours, veuillez patienter...');
        
        try {
            const data = new FormData();
            data.append('file', formData.file);
            data.append('title', formData.title || formData.file.name.replace('.pdf', ''));
            data.append('description', formData.description || '');
            data.append('catalog_type', formData.catalog_type);
            
            await catalogsAPI.upload(data);
            toast.success('Catalogue uploadé avec succès !');
            setIsUploadDialogOpen(false);
            setFormData({ title: '', description: '', catalog_type: 'catalog', file: null });
            fetchCatalogs();
        } catch (error) {
            console.error('Upload error:', error);
            const errorMessage = error.response?.data?.detail || error.message || 'Erreur lors de l\'upload';
            toast.error(errorMessage);
        } finally {
            setUploading(false);
        }
    };

    const handleEdit = async (e) => {
        e.preventDefault();
        try {
            await catalogsAPI.update(selectedCatalog.id, {
                title: formData.title,
                description: formData.description,
                catalog_type: formData.catalog_type
            });
            toast.success('Catalogue mis à jour');
            setIsEditDialogOpen(false);
            fetchCatalogs();
        } catch (error) {
            toast.error('Erreur lors de la mise à jour');
        }
    };

    const handleToggleActive = async (catalog) => {
        try {
            await catalogsAPI.update(catalog.id, { is_active: !catalog.is_active });
            toast.success(catalog.is_active ? 'Catalogue masqué' : 'Catalogue activé');
            fetchCatalogs();
        } catch (error) {
            toast.error('Erreur lors de la mise à jour');
        }
    };

    const handleDelete = async () => {
        try {
            await catalogsAPI.delete(selectedCatalog.id);
            toast.success('Catalogue supprimé');
            setIsDeleteDialogOpen(false);
            fetchCatalogs();
        } catch (error) {
            toast.error('Erreur lors de la suppression');
        }
    };

    const openEditDialog = (catalog) => {
        setSelectedCatalog(catalog);
        setFormData({
            title: catalog.title,
            description: catalog.description || '',
            catalog_type: catalog.catalog_type,
            file: null
        });
        setIsEditDialogOpen(true);
    };

    const openDeleteDialog = (catalog) => {
        setSelectedCatalog(catalog);
        setIsDeleteDialogOpen(true);
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-sand flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-jungle"></div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-sand" data-testid="admin-catalogs-page">
            <div className="max-w-7xl mx-auto p-6">
                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
                    <h1 className="font-heading text-3xl font-bold tracking-tight">
                        Gestion des Catalogues
                    </h1>
                    <Button 
                        onClick={() => setIsUploadDialogOpen(true)}
                        className="bg-jungle hover:bg-jungle/90 text-sand rounded-xl"
                        data-testid="upload-catalog-button"
                    >
                        <Upload className="h-4 w-4 mr-2" />
                        Uploader un PDF
                    </Button>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
                    <Card>
                        <CardContent className="pt-6">
                            <div className="flex items-center gap-4">
                                <div className="p-3 bg-blue-100 rounded-xl">
                                    <FileText className="h-6 w-6 text-blue-600" />
                                </div>
                                <div>
                                    <p className="text-2xl font-bold">{catalogs.length}</p>
                                    <p className="text-sm text-muted-foreground">Total catalogues</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="pt-6">
                            <div className="flex items-center gap-4">
                                <div className="p-3 bg-green-100 rounded-xl">
                                    <Eye className="h-6 w-6 text-green-600" />
                                </div>
                                <div>
                                    <p className="text-2xl font-bold">{catalogs.filter(c => c.is_active).length}</p>
                                    <p className="text-sm text-muted-foreground">Actifs</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="pt-6">
                            <div className="flex items-center gap-4">
                                <div className="p-3 bg-purple-100 rounded-xl">
                                    <Download className="h-6 w-6 text-purple-600" />
                                </div>
                                <div>
                                    <p className="text-2xl font-bold">
                                        {formatFileSize(catalogs.reduce((acc, c) => acc + (c.file_size || 0), 0))}
                                    </p>
                                    <p className="text-sm text-muted-foreground">Espace utilisé</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </div>

                {/* Table */}
                <Card>
                    <CardHeader>
                        <CardTitle>Liste des catalogues</CardTitle>
                    </CardHeader>
                    <CardContent>
                        {catalogs.length === 0 ? (
                            <div className="text-center py-12">
                                <FolderOpen className="h-16 w-16 text-muted-foreground mx-auto mb-4 opacity-50" />
                                <p className="text-muted-foreground">Aucun catalogue uploadé</p>
                                <Button 
                                    onClick={() => setIsUploadDialogOpen(true)}
                                    className="mt-4 bg-jungle hover:bg-jungle/90"
                                >
                                    <Upload className="h-4 w-4 mr-2" />
                                    Uploader votre premier catalogue
                                </Button>
                            </div>
                        ) : (
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>Titre</TableHead>
                                        <TableHead>Type</TableHead>
                                        <TableHead>Fichier</TableHead>
                                        <TableHead>Taille</TableHead>
                                        <TableHead>Date</TableHead>
                                        <TableHead>Statut</TableHead>
                                        <TableHead className="text-right">Actions</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {catalogs.map((catalog) => (
                                        <TableRow key={catalog.id} data-testid={`catalog-row-${catalog.id}`}>
                                            <TableCell>
                                                <div className="font-medium">{catalog.title}</div>
                                                {catalog.description && (
                                                    <div className="text-sm text-muted-foreground truncate max-w-xs">
                                                        {catalog.description}
                                                    </div>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <Badge className={CATALOG_TYPES[catalog.catalog_type]?.color}>
                                                    {CATALOG_TYPES[catalog.catalog_type]?.label || catalog.catalog_type}
                                                </Badge>
                                            </TableCell>
                                            <TableCell>
                                                <span className="text-sm text-muted-foreground">
                                                    {catalog.original_filename}
                                                </span>
                                            </TableCell>
                                            <TableCell>{formatFileSize(catalog.file_size)}</TableCell>
                                            <TableCell>{formatDate(catalog.created_at)}</TableCell>
                                            <TableCell>
                                                <Badge className={catalog.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}>
                                                    {catalog.is_active ? 'Actif' : 'Masqué'}
                                                </Badge>
                                            </TableCell>
                                            <TableCell>
                                                <div className="flex justify-end gap-1">
                                                    <Button 
                                                        variant="ghost" 
                                                        size="sm"
                                                        onClick={() => handleToggleActive(catalog)}
                                                        className={catalog.is_active ? 'hover:bg-red-100' : 'hover:bg-green-100'}
                                                        title={catalog.is_active ? 'Désactiver le catalogue' : 'Activer le catalogue'}
                                                        data-testid={`toggle-catalog-${catalog.id}`}
                                                    >
                                                        {catalog.is_active ? (
                                                            <EyeOff className="h-4 w-4 text-red-500" />
                                                        ) : (
                                                            <Eye className="h-4 w-4 text-green-600" />
                                                        )}
                                                    </Button>
                                                    <Button 
                                                        variant="ghost" 
                                                        size="sm"
                                                        onClick={() => openEditDialog(catalog)}
                                                        className="hover:bg-leaf/10"
                                                        title="Modifier"
                                                    >
                                                        <Pencil className="h-4 w-4 text-leaf" />
                                                    </Button>
                                                    <Button 
                                                        variant="ghost" 
                                                        size="sm"
                                                        className="text-destructive hover:text-destructive hover:bg-destructive/10"
                                                        onClick={() => openDeleteDialog(catalog)}
                                                        title="Supprimer"
                                                    >
                                                        <Trash2 className="h-4 w-4" />
                                                    </Button>
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        )}
                    </CardContent>
                </Card>
            </div>

            {/* Upload Dialog */}
            <Dialog open={isUploadDialogOpen} onOpenChange={setIsUploadDialogOpen}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle>Uploader un catalogue</DialogTitle>
                        <DialogDescription>
                            Uploadez un fichier PDF (catalogue, flyer ou affiche) - Max 50 Mo
                        </DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleUpload}>
                        <div className="space-y-4 py-4">
                            <div className="space-y-2">
                                <Label>Fichier PDF *</Label>
                                <Input
                                    type="file"
                                    accept=".pdf,application/pdf"
                                    onChange={(e) => {
                                        const file = e.target.files[0];
                                        if (file) {
                                            setFormData({ ...formData, file, title: formData.title || file.name.replace('.pdf', '') });
                                            toast.info(`Fichier sélectionné: ${file.name} (${formatFileSize(file.size)})`);
                                        }
                                    }}
                                    className="cursor-pointer"
                                    data-testid="catalog-file-input"
                                />
                                {formData.file && (
                                    <p className="text-sm text-green-600">
                                        ✓ Fichier: {formData.file.name} ({formatFileSize(formData.file.size)})
                                    </p>
                                )}
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="title">Titre</Label>
                                <Input
                                    id="title"
                                    value={formData.title}
                                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                                    placeholder="Nom du catalogue"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="type">Type</Label>
                                <Select
                                    value={formData.catalog_type}
                                    onValueChange={(value) => setFormData({ ...formData, catalog_type: value })}
                                >
                                    <SelectTrigger>
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="catalog">Catalogue</SelectItem>
                                        <SelectItem value="flyer">Flyer</SelectItem>
                                        <SelectItem value="poster">Affiche</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="description">Description</Label>
                                <Textarea
                                    id="description"
                                    value={formData.description}
                                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                    placeholder="Description du catalogue (optionnel)"
                                    rows={3}
                                />
                            </div>
                        </div>
                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => setIsUploadDialogOpen(false)}>
                                Annuler
                            </Button>
                            <Button type="submit" className="bg-jungle hover:bg-jungle/90" disabled={uploading}>
                                {uploading ? 'Upload en cours...' : 'Uploader'}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Edit Dialog */}
            <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle>Modifier le catalogue</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleEdit}>
                        <div className="space-y-4 py-4">
                            <div className="space-y-2">
                                <Label htmlFor="edit-title">Titre</Label>
                                <Input
                                    id="edit-title"
                                    value={formData.title}
                                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="edit-type">Type</Label>
                                <Select
                                    value={formData.catalog_type}
                                    onValueChange={(value) => setFormData({ ...formData, catalog_type: value })}
                                >
                                    <SelectTrigger>
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="catalog">Catalogue</SelectItem>
                                        <SelectItem value="flyer">Flyer</SelectItem>
                                        <SelectItem value="poster">Affiche</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="edit-description">Description</Label>
                                <Textarea
                                    id="edit-description"
                                    value={formData.description}
                                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                    rows={3}
                                />
                            </div>
                        </div>
                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => setIsEditDialogOpen(false)}>
                                Annuler
                            </Button>
                            <Button type="submit" className="bg-jungle hover:bg-jungle/90">
                                Enregistrer
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Delete Dialog */}
            <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Supprimer le catalogue</DialogTitle>
                        <DialogDescription>
                            Êtes-vous sûr de vouloir supprimer "{selectedCatalog?.title}" ? Cette action est irréversible.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setIsDeleteDialogOpen(false)}>
                            Annuler
                        </Button>
                        <Button variant="destructive" onClick={handleDelete}>
                            Supprimer
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};
